import { useMemo, useState } from 'react'
import { ArrowsClockwise, Flask, Plus, Trash } from '@phosphor-icons/react'
import { Badge, Banner, ChoiceGroup, DataTable, EmptyState, Field, Input, Select, type Column } from '@/ui'
import { errorMessage } from '../api/core'
import { localToday, useCreateLabLimit, useLabLimits, useRetireLabLimit, type LabLimitRow } from '../api/masterData'
import { useCrops } from '../api/reference'
import { ActionButton } from '../components/Can'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { FilterBar, SelectFilter } from '../components/FilterBar'
import { FormDialog } from '../components/FormDialog'
import { ConsolePage } from '../components/Layout'
import { useUrlState } from '../lib/useUrlState'
import {
  createLabLimitBody,
  draftFromLabLimit,
  emptyLabLimitDraft,
  labLimitOk,
  limitStates,
  trimDecimal,
  validateLabLimitDraft,
  type LabLimitDraft,
  type LabLimitRule,
  type LimitState,
} from '../model/labLimitForm'
import { serverProblem } from '../model/serverError'

const EVERY_CROP = '*'

const STATE_BADGE: Record<LimitState, { tone: 'ok' | 'info' | 'neutral'; label: string }> = {
  IN_FORCE: { tone: 'ok', label: 'In force' },
  SCHEDULED: { tone: 'info', label: 'From a later date' },
  SUPERSEDED: { tone: 'neutral', label: 'Older version' },
}

type Row = LabLimitRow & { state: LimitState }

/**
 * Lab limits per crop, panel and analyte (F7.5-b). A lab report row whose analyte has no limit
 * (and whose panel has no default) is refused with 422 NO_LIMIT_FOR_ANALYTE, so every analyte a
 * lab reports needs a limit here first. Limits are versioned, never edited.
 */
export function LabLimitsPage() {
  const [f, setF, { reset, active }] = useUrlState({ cropId: '', panel: '', release: '', history: '' })
  const q = useLabLimits({
    cropId: f.cropId || undefined,
    panel: f.panel || undefined,
    requiredForRelease: f.release === 'yes' ? true : undefined,
  })
  const crops = useCrops()
  const today = localToday()
  const [adding, setAdding] = useState<{ draft: LabLimitDraft; newVersion: boolean } | null>(null)
  const [retiring, setRetiring] = useState<Row | null>(null)

  const all = useMemo<Row[]>(() => {
    const data = q.data?.rows ?? []
    const states = limitStates(data, today)
    return data.map((r) => ({ ...r, state: states.get(r.id) ?? 'IN_FORCE' }))
  }, [q.data, today])
  const rows = f.history === 'yes' ? all : all.filter((r) => r.state !== 'SUPERSEDED')
  const panels = useMemo(() => [...new Set(all.map((r) => r.panel))].sort(), [all])

  const columns: Column<Row>[] = [
    { id: 'panel', header: 'Panel', cell: (r) => <span className="font-mono">{r.panel}</span>, sortValue: (r) => r.panel },
    {
      id: 'analyte',
      header: 'Analyte',
      cell: (r) => (r.analyte ? <span className="font-semibold">{r.analyte}</span> : <span className="text-muted">Any analyte (panel default)</span>),
      sortValue: (r) => r.analyte ?? '',
    },
    { id: 'crop', header: 'Crop', cell: (r) => r.cropName ?? <span className="text-muted">Every crop</span>, sortValue: (r) => r.cropName ?? '' },
    {
      id: 'limit',
      header: 'Limit',
      cell: (r) =>
        r.rule === 'MAX_LIMIT' ? (
          <span className="font-mono">
            ≤ {trimDecimal(r.limitValue)} {r.uom}
          </span>
        ) : (
          <span>
            Not detected
            {r.maxLoq && (
              <span className="block text-[12px] text-muted">
                LOQ ≤ {trimDecimal(r.maxLoq)} {r.uom}
              </span>
            )}
          </span>
        ),
    },
    {
      id: 'release',
      header: 'Needed for release',
      hideBelow: 'md',
      cell: (r) => (r.requiredForRelease ? <Badge tone="warn">Required</Badge> : <span className="text-muted">No</span>),
    },
    {
      id: 'version',
      header: 'Version',
      hideBelow: 'sm',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <span>
            v{r.version} · from {r.effectiveFrom}
          </span>
          <span className="text-[12px]">
            <Badge tone={STATE_BADGE[r.state].tone}>{STATE_BADGE[r.state].label}</Badge>
          </span>
        </span>
      ),
      sortValue: (r) => r.version,
    },
    { id: 'source', header: 'Source', hideBelow: 'lg', cell: (r) => <span className="text-muted">{r.source}</span> },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (r) => (
        <span className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <ActionButton perm="lab-limits:create" hide size="sm" variant="ghost" leadingIcon={<ArrowsClockwise />} onClick={() => setAdding({ draft: draftFromLabLimit(r, today), newVersion: true })}>
            New version
          </ActionButton>
          <ActionButton perm="lab-limits:delete" hide size="sm" variant="ghost" leadingIcon={<Trash />} onClick={() => setRetiring(r)}>
            Retire
          </ActionButton>
        </span>
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Setup"
      title="Lab limits"
      description="The limit each analyte is judged against, per crop and test panel. A lab report with an analyte that has no limit here (and no panel default) is refused, so add limits before the first report comes in. A change is saved as a new version from a date; reports keep the limit they were judged on."
      actions={
        <ActionButton perm="lab-limits:create" leadingIcon={<Plus />} onClick={() => setAdding({ draft: { ...emptyLabLimitDraft(today), cropId: f.cropId, panel: f.panel }, newVersion: false })}>
          Add limit
        </ActionButton>
      }
    >
      <DataTable<Row>
        caption="Lab limits"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        searchText={(r) => `${r.analyte ?? ''} ${r.panel} ${r.cropName ?? ''} ${r.source}`}
        searchPlaceholder="Analyte, panel or source"
        pageSize={100}
        empty={
          <EmptyState
            icon={<Flask />}
            title={active ? 'No limits match' : 'No lab limits yet'}
            description="Add a panel default (no analyte) to cover every analyte of a panel, then analyte limits where they differ."
            compact
          />
        }
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Crop"
              value={f.cropId}
              onChange={(cropId) => setF({ cropId })}
              options={(crops.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
            <SelectFilter label="Panel" value={f.panel} onChange={(panel) => setF({ panel })} options={panels.map((p) => ({ value: p, label: p }))} />
            <SelectFilter
              label="Needed for release"
              value={f.release as 'yes' | ''}
              onChange={(release) => setF({ release })}
              options={[{ value: 'yes', label: 'Required only' }]}
            />
            <SelectFilter
              label="Versions"
              value={f.history as 'yes' | ''}
              onChange={(history) => setF({ history })}
              options={[{ value: 'yes', label: 'Include older versions' }]}
            />
          </FilterBar>
        }
      />
      {q.data && q.data.total > q.data.rows.length && (
        <Banner tone="info">
          Showing {q.data.rows.length} of {q.data.total} limits. Filter by crop or panel to see the rest.
        </Banner>
      )}
      {adding && <LabLimitDialog initial={adding.draft} isNewVersion={adding.newVersion} crops={crops.data ?? []} onClose={() => setAdding(null)} />}
      <ConfirmRetire row={retiring} onClose={() => setRetiring(null)} />
    </ConsolePage>
  )
}

function LabLimitDialog({
  initial,
  isNewVersion,
  crops,
  onClose,
}: {
  initial: LabLimitDraft
  isNewVersion: boolean
  crops: ReadonlyArray<{ id: string; name: string }>
  onClose: () => void
}) {
  const [d, setD] = useState<LabLimitDraft>(initial)
  const [touched, setTouched] = useState<Set<keyof LabLimitDraft>>(new Set())
  const create = useCreateLabLimit({ inline: true })
  const p = validateLabLimitDraft(d)
  const touch = (k: keyof LabLimitDraft) => setTouched((s) => new Set(s).add(k))
  const shown = (k: keyof typeof p) => (touched.has(k) ? p[k] : undefined)
  const err = create.isError ? serverProblem(create.error) : null

  return (
    <FormDialog
      open
      onClose={onClose}
      title={isNewVersion ? `New version · ${initial.panel}${initial.analyte ? ` · ${initial.analyte}` : ''}` : 'Add lab limit'}
      description={
        isNewVersion
          ? 'Saved as the next version for this crop, panel and analyte. Reports tested on or after the date use it; older reports keep theirs.'
          : undefined
      }
      saveLabel="Save limit"
      loading={create.isPending}
      canSave={labLimitOk(p)}
      onSave={() => create.mutateAsync(createLabLimitBody(d))}
    >
      {err && (
        <Banner tone="error" title={err.message}>
          {err.details.join(' · ')}
        </Banner>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Crop" hint="Every crop: applies unless a crop has its own row">
          <Select
            value={d.cropId || EVERY_CROP}
            onChange={(v) => setD({ ...d, cropId: v === EVERY_CROP ? '' : v })}
            options={[{ value: EVERY_CROP, label: 'Every crop' }, ...crops.map((c) => ({ value: c.id, label: c.name }))]}
          />
        </Field>
        <Field label="Panel" required error={shown('panel')} hint="As the lab names it, e.g. PESTICIDE">
          <Input
            value={d.panel}
            onChange={(e) => setD({ ...d, panel: e.target.value.toUpperCase() })}
            onBlur={() => touch('panel')}
            maxLength={40}
            placeholder="PESTICIDE"
            className="font-mono"
            data-autofocus
          />
        </Field>
        <Field label="Analyte" error={shown('analyte')} hint="Empty = default for the whole panel">
          <Input
            value={d.analyte}
            onChange={(e) => setD({ ...d, analyte: e.target.value })}
            onBlur={() => touch('analyte')}
            maxLength={80}
            placeholder="Chlorpyrifos"
          />
        </Field>
      </div>
      <ChoiceGroup<LabLimitRule>
        label="Rule"
        value={d.rule}
        onChange={(rule) => setD({ ...d, rule })}
        options={[
          { value: 'NOT_DETECTED', label: 'Must not be detected' },
          { value: 'MAX_LIMIT', label: 'At most a limit' },
        ]}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Limit" required={d.rule === 'MAX_LIMIT'} error={shown('limitValue') ?? (d.limitValue ? p.limitValue : undefined)} hint={d.rule === 'MAX_LIMIT' ? 'Over this fails' : 'Optional for “not detected”'}>
          <Input
            inputMode="decimal"
            value={d.limitValue}
            onChange={(e) => setD({ ...d, limitValue: e.target.value })}
            onBlur={() => touch('limitValue')}
            placeholder="0.01"
            className="font-mono"
          />
        </Field>
        <Field label="Highest LOQ accepted" error={d.maxLoq ? p.maxLoq : undefined} hint="A “not detected” with a higher LOQ cannot back the claim">
          <Input
            inputMode="decimal"
            value={d.maxLoq}
            onChange={(e) => setD({ ...d, maxLoq: e.target.value })}
            onBlur={() => touch('maxLoq')}
            placeholder="0.01"
            className="font-mono"
          />
        </Field>
        <Field label="Unit" required error={shown('uom')}>
          <Input value={d.uom} onChange={(e) => setD({ ...d, uom: e.target.value })} onBlur={() => touch('uom')} maxLength={10} placeholder="mg/kg" />
        </Field>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Applies from" required error={p.effectiveFrom} hint="Reports are judged by the limit in force on their test date">
          <Input type="date" value={d.effectiveFrom} onChange={(e) => setD({ ...d, effectiveFrom: e.target.value })} />
        </Field>
        <ChoiceGroup<'yes' | 'no'>
          label="Needed for release"
          value={d.requiredForRelease ? 'yes' : 'no'}
          onChange={(v) => setD({ ...d, requiredForRelease: v === 'yes' })}
          options={[
            { value: 'no', label: 'No' },
            { value: 'yes', label: 'Yes, must be tested' },
          ]}
        />
      </div>
      <Field label="Source" required error={shown('source')} hint="Regulation or buyer spec, e.g. FSSAI Contaminants Regulations 2011, 2.1">
        <Input value={d.source} onChange={(e) => setD({ ...d, source: e.target.value })} onBlur={() => touch('source')} maxLength={120} />
      </Field>
    </FormDialog>
  )
}

function ConfirmRetire({ row, onClose }: { row: Row | null; onClose: () => void }) {
  const retire = useRetireLabLimit()
  return (
    <ConfirmDialog
      open={Boolean(row)}
      onClose={onClose}
      title={`Retire v${row?.version ?? ''} of ${row?.panel ?? ''}${row?.analyte ? ` · ${row.analyte}` : ''}?`}
      description={
        row?.state === 'IN_FORCE'
          ? 'If no older version or panel default covers this analyte, new reports with it will be refused until a limit is added. Reports already judged keep their limit.'
          : 'Reports already judged keep their limit.'
      }
      confirmLabel="Retire limit"
      tone="danger"
      loading={retire.isPending}
      onConfirm={async () => {
        if (!row) return
        await retire.mutateAsync(row.id)
        onClose()
      }}
    />
  )
}
